import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Autocomplete,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { apiService } from '../services/api';
import { CURRENCIES } from '../data/constants';

interface Entity {
  id: string;
  name: string;
  code: string;
}

interface CreatePayrollRunModalProps {
  open: boolean;
  onClose: () => void;
  onPayrollRunCreated: () => void;
}

const CreatePayrollRunModal: React.FC<CreatePayrollRunModalProps> = ({
  open,
  onClose,
  onPayrollRunCreated,
}) => {
  const [formData, setFormData] = useState({
    entityId: '',
    payrollCalendarId: '',
    periodName: '',
    payDate: new Date().toISOString().split('T')[0],
    periodStartDate: '',
    periodEndDate: '',
    currency: 'HKD',
    notes: '',
  });

  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    if (open) {
      loadEntities();
      generatePeriodName();
    }
  }, [open]);

  const loadEntities = async () => {
    try {
      setDataLoading(true);
      const entitiesData = await apiService.getEntities();
      setEntities(entitiesData);
    } catch (err) {
      console.error('Error loading entities:', err);
      setError('Failed to load entities');
    } finally {
      setDataLoading(false);
    }
  };

  const generatePeriodName = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.toLocaleString('default', { month: 'long' });
    setFormData(prev => ({
      ...prev,
      periodName: `${month} ${year}`,
      periodStartDate: new Date(year, now.getMonth(), 1).toISOString().split('T')[0],
      periodEndDate: new Date(year, now.getMonth() + 1, 0).toISOString().split('T')[0],
    }));
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      const payrollData = {
        entityId: formData.entityId,
        periodName: formData.periodName,
        periodStartDate: formData.periodStartDate,
        periodEndDate: formData.periodEndDate,
        payDate: formData.payDate,
        currency: formData.currency,
        notes: formData.notes,
      };

      await apiService.createPayrollRun(payrollData);

      onPayrollRunCreated();
      onClose();
      setFormData({
        entityId: '',
        payrollCalendarId: '',
        periodName: '',
        payDate: new Date().toISOString().split('T')[0],
        periodStartDate: '',
        periodEndDate: '',
        currency: 'HKD',
        notes: '',
      });
    } catch (err: any) {
      console.error('Error creating payroll run:', err);
      setError(err.message || 'Failed to create payroll run');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formData.entityId && formData.periodName && formData.payDate && 
                     formData.periodStartDate && formData.periodEndDate && formData.currency;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Payroll Run</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {dataLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <Autocomplete
                options={entities}
                getOptionLabel={(option) => option.name}
                value={entities.find(e => e.id === formData.entityId) || null}
                onChange={(event, newValue) => {
                  handleInputChange('entityId', newValue?.id || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Entity"
                    required
                    helperText="Select company entity"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth required>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={formData.currency}
                  onChange={(e) => handleInputChange('currency', e.target.value)}
                  label="Currency"
                >
                  {CURRENCIES.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.symbol ? `${c.code} — ${c.name} (${c.symbol})` : `${c.code} — ${c.name}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Period Name"
                value={formData.periodName}
                onChange={(e) => handleInputChange('periodName', e.target.value)}
                placeholder="e.g., January 2024"
                required
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                label="Period Start Date"
                type="date"
                value={formData.periodStartDate}
                onChange={(e) => handleInputChange('periodStartDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                label="Period End Date"
                type="date"
                value={formData.periodEndDate}
                onChange={(e) => handleInputChange('periodEndDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: formData.periodStartDate }}
                required
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Pay Date"
                type="date"
                value={formData.payDate}
                onChange={(e) => handleInputChange('payDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="When employees will be paid"
                required
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Optional description for this payroll run"
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !isFormValid || dataLoading}
        >
          {loading ? <CircularProgress size={20} /> : 'Create Payroll Run'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePayrollRunModal;